import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import {
  connectionRows,
  CreateWebhookDocument,
  type CreateWebhookInput,
  DeleteWebhookDocument,
  GetStageForWebhookViewDocument,
  GetStagesForWorkflowDocument,
  GetWebhooksDocument,
  type StageOption,
  UpdateWebhookDocument,
  type WebhookRow,
  type UpdateWebhookInput,
} from "#app/lib/data";
import { addWebhookToCache, removeWebhookFromCache } from "./webhook-cache-utils";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useAnalytics, AnalyticsContext, SettingsEvent } from "#app/lib/analytics";

export type WebhookItem = WebhookRow;

const WEBHOOKS_VARIABLES = { limit: 100 };

export function useWebhooks() {
  const { data, loading, error } = useQuery(GetWebhooksDocument, {
    variables: WEBHOOKS_VARIABLES,
    fetchPolicy: "cache-and-network",
  });

  const connection = data?.webhooks;
  const webhooks = useMemo(
    () => connectionRows(connection),
    [connection],
  );

  return {
    webhooks,
    total: connection?.total ?? 0,
    isLoading: loading,
    error,
  };
}

// The webhook trigger picker's stages. GetStagesForWorkflow spreads
// StageOption_stage, so this is the same `StageOption` lib/data exports.
export type { StageOption };

export function useStagesForWorkflow(workflowId: string | null) {
  const variables = useMemo(
    () => ({ workflowId: workflowId!, limit: 100 }),
    [workflowId],
  );

  const { data, loading } = useQuery(GetStagesForWorkflowDocument, {
    variables,
    skip: !workflowId,
    fetchPolicy: "cache-and-network",
  });

  const stages = useMemo(
    () =>
      connectionRows(data?.stages) as StageOption[],
    [data],
  );

  return { stages, isLoading: loading };
}

export function useWebhookTargetLabel(
  when: Record<string, any> | null | undefined,
) {
  const stageId =
    when?.stageId && typeof when.stageId === "string" ? when.stageId : null;

  const variables = useMemo(
    () => ({ where: { stageId: { eq: stageId } }, limit: 1 }),
    [stageId],
  );

  const { data, loading } = useQuery(GetStageForWebhookViewDocument, {
    variables,
    skip: !stageId,
    fetchPolicy: "cache-and-network",
  });

  if (!stageId) return null;
  if (loading && !data) return null;

  const stage = data?.stages?.edges?.[0]?.node;
  if (!stage) return null;

  const workflow = stage.workflow?.name;
  return workflow ? `${workflow} \u2022 ${stage.name}` : stage.name;
}

export function useWebhookMutations() {
  const { track } = useAnalytics();
  const [createMutation] = useMutation(CreateWebhookDocument);
  const [updateMutation] = useMutation(UpdateWebhookDocument);
  const [deleteMutation] = useMutation(DeleteWebhookDocument);

  const createWebhook = useCallback(
    async (input: CreateWebhookInput) => {
      try {
        const { data } = await createMutation({
          variables: { input },
          update(cache, { data }) {
            const result = data?.createWebhook;
            if (result?.__typename !== "Webhook") return;
            addWebhookToCache(cache, result);
          },
        });

        const result = data?.createWebhook;
        if (result?.__typename === "Webhook") {
          track({ context: AnalyticsContext.Settings, event: SettingsEvent.Create, label: "webhook", data: { resource: result.resource, trigger: result.trigger, method: result.method } });
          toastManager.add({ title: "Webhook created", type: "success" });
          return result;
        }
        toastManager.add({ title: "Failed to create webhook", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to create webhook");
        return null;
      }
    },
    [createMutation, track],
  );

  const updateWebhook = useCallback(
    async (id: string, input: UpdateWebhookInput) => {
      try {
        const { data } = await updateMutation({
          variables: { id, input },
        });

        const result = data?.updateWebhook;
        if (result?.__typename === "Webhook") {
          track({ context: AnalyticsContext.Settings, event: SettingsEvent.Update, label: "webhook" });
          toastManager.add({ title: "Webhook updated", type: "success" });
          return result;
        }
        toastManager.add({ title: "Failed to update webhook", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to update webhook");
        return null;
      }
    },
    [updateMutation, track],
  );

  const deleteWebhook = useCallback(
    async (webhookId: string) => {
      try {
        await deleteMutation({
          variables: { id: webhookId },
          update(cache, { data }) {
            const result = data?.deleteWebhook;
            if (result?.__typename !== "Webhook") return;
            removeWebhookFromCache(cache, result.webhookId);
            const cacheId = cache.identify(result);
            // `identify` answers `undefined` for an object it cannot key, and
            // the cache reads an absent `id` as the ROOT — the eviction would
            // drop a ROOT_QUERY field instead of this entity.
            if (cacheId) cache.evict({ id: cacheId });
            cache.gc();
          },
        });
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Delete, label: "webhook" });
        toastManager.add({ title: "Webhook deleted", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to delete webhook");
      }
    },
    [deleteMutation, track],
  );

  const toggleWebhookArchived = useCallback(
    async (webhook: WebhookItem) => {
      const result = await updateWebhook(webhook.webhookId, {
        archived: !webhook.archived,
      });
      if (result) {
        track({ context: AnalyticsContext.Settings, event: webhook.archived ? SettingsEvent.Unarchive : SettingsEvent.Archive, label: "webhook", data: { resource: webhook.resource, trigger: webhook.trigger } });
      }
      return result;
    },
    [updateWebhook, track],
  );

  return { createWebhook, updateWebhook, deleteWebhook, toggleWebhookArchived };
}
